import {
  Event,
  CreateEventRequest,
  UpdateEventRequest
} from '../../types/calendar-event';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc,
  writeBatch,
  onSnapshot,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getCurrentUserId } from '../auth';
import { eventBus, EVENTS } from '../../utils/eventBus';
import { logger } from '../../utils/logger'; 

// Firestore 集合名稱
const COLLECTION_NAME = 'events';

// 即時監聽回調接口
interface EventSubscriptionCallbacks {
  onSnapshot: (events: Event[]) => void;
  onError: (error: Error) => void;
}

// 轉換 Firebase 時間戳為 Date 對象
const timestampToDate = (timestamp: Timestamp | Date | number | string): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  return new Date(timestamp);
};

// 將 Firebase 文件轉換為 Event 對象
const convertDocToEvent = (doc: QueryDocumentSnapshot<DocumentData>): Event => {
  const data = doc.data();
  return {
    id: doc.id,
    itemType: 'event',
    userId: data.userId || '',
    title: data.title || '',
    description: data.description || '',
    timeType: data.timeType || 'timed',
    category: data.category || 'other',
    startTime: timestampToDate(data.startTime),
    endTime: data.endTime
      ? timestampToDate(data.endTime)
      : (() => {
          const start = timestampToDate(data.startTime);
          const end = new Date(start);

          if (data.timeType === 'allday') {
            end.setHours(23, 59, 59, 999);
          } else {
            end.setHours(start.getHours() + 1);
          }

          logger.warn('calendar-events: Missing endTime in event data, using fallback');

          return end;
        })(),
    preparation: data.preparation,
    source: data.source || 'manual',
    location: data.location || '',
    // Fallback: 優先使用 notes，若無則使用 description（向後相容）
    notes: data.notes || data.description || '',
    createdAt: timestampToDate(data.createdAt),
    updatedAt: data.updatedAt ? timestampToDate(data.updatedAt) : undefined,
  };
};

// 清理 undefined 字段的工具函數
const cleanUndefinedFields = (obj: Record<string, unknown>): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
};

// 轉換輸入為 Firestore 格式
const convertToFirestoreData = (eventData: CreateEventRequest | UpdateEventRequest): Record<string, unknown> => {
  // 先清理 undefined 字段
  const cleanedData = cleanUndefinedFields(eventData as Record<string, unknown>);
  const data: Record<string, unknown> = {
    itemType: 'event',  // 明確設置類型標識
    ...cleanedData,
  };

  // 處理 startTime - 只有當它存在時才轉換
  if (eventData.startTime !== undefined) {
    data.startTime = eventData.startTime instanceof Date 
      ? Timestamp.fromDate(eventData.startTime) 
      : Timestamp.fromDate(new Date(eventData.startTime));
  }
  
  // 處理 endTime - 只有當它存在時才轉換
  if (eventData.endTime !== undefined) {
    data.endTime = eventData.endTime instanceof Date
      ? Timestamp.fromDate(eventData.endTime)
      : Timestamp.fromDate(new Date(eventData.endTime));
  }

  return data;
};

/**
 * 創建新事件
 */
const createEvent = async (userId: string, eventData: CreateEventRequest): Promise<string> => {
  try {
    const now = new Date();
    const { userId: _, ...firestoreData } = convertToFirestoreData(eventData);
    const docData = {
      ...firestoreData,
      userId,
        createdAt: Timestamp.fromDate(now),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), docData);
    
    // 發布事件
    eventBus.emit(EVENTS.EVENT.CREATED, {
      id: docRef.id,
      ...eventData
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

/**
 * 獲取指定日期範圍內的事件
 * 
 * 注意：由於 Firestore 限制，我們無法直接查詢跨日事件
 * 策略：查詢更大的範圍，然後在客戶端過濾
 */
const getEventsByDateRange = async (
  userId: string, 
  startDate: Date, 
  endDate: Date
): Promise<Event[]> => {
  try {
    // 擴大查詢範圍：向前推30天以捕獲可能的跨日事件
    const extendedStartDate = new Date(startDate);
    extendedStartDate.setDate(extendedStartDate.getDate() - 30);
    
    const startTimestamp = Timestamp.fromDate(extendedStartDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('startTime', '>=', startTimestamp),
      where('startTime', '<=', endTimestamp),
      orderBy('startTime', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const allEvents = querySnapshot.docs.map(convertDocToEvent);
    
    // 客戶端過濾：保留與目標日期範圍有重疊的事件
    return allEvents.filter(event => {
      const eventStart = event.startTime;
      const eventEnd = event.endTime || event.startTime;
      
      // 檢查事件是否與目標日期範圍有重疊
      return eventStart <= endDate && eventEnd >= startDate;
    });
  } catch (error) {
    console.error('Error getting events by date range:', error);
    throw error;
  }
};

/**
 * 獲取指定日期的事件
 */
const getEventsByDate = async (userId: string, date: Date): Promise<Event[]> => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  
  return getEventsByDateRange(userId, startOfDay, endOfDay);
};

/**
 * 根據 ID 獲取單個事件
 */
const getEventById = async (eventId: string): Promise<Event | null> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, eventId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return convertDocToEvent(docSnap);
    }
    return null;
  } catch (error) {
    console.error('Error getting event by id:', error);
    throw error;
  }
};

/**
 * 更新事件
 */
const updateEvent = async (eventId: string, updates: UpdateEventRequest): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, eventId);
    const { id: _, ...firestoreData } = convertToFirestoreData(updates);
    const updateData = {
      ...firestoreData,
      updatedAt: Timestamp.fromDate(new Date()),
    };
    
    await updateDoc(docRef, updateData);
    
    // 發布事件
    eventBus.emit(EVENTS.EVENT.UPDATED, {
      ...updates,
      id: eventId
    });
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

/**
 * 刪除事件
 */
const deleteEvent = async (eventId: string): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, eventId);
    await deleteDoc(docRef);
    
    // 發布事件
    eventBus.emit(EVENTS.EVENT.DELETED, { id: eventId });
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

/**
 * 批量創建事件
 */
const createEventsBatch = async (userId: string, events: CreateEventRequest[]): Promise<string[]> => {
  try {
    const batch = writeBatch(db);
    const eventIds: string[] = [];
    const now = new Date();

    for (const eventData of events) {
      const docRef = doc(collection(db, COLLECTION_NAME));
      const docData = {
        ...convertToFirestoreData(eventData),
        userId,
            createdAt: Timestamp.fromDate(now),
      };

      batch.set(docRef, docData);
      eventIds.push(docRef.id);
    }

    await batch.commit();
    
    // 發布批量創建事件
    eventBus.emit(EVENTS.EVENT.BATCH_CREATED, {
      eventIds,
      count: events.length,
      userId
    });
    
    return eventIds;
  } catch (error) {
    console.error('Error creating events batch:', error);
    throw error;
  }
};

/**
 * 檢查時間衝突的事件
 */
const checkEventConflicts = async (
  userId: string,
  startTime: Date,
  endTime: Date,
  excludeEventId?: string
): Promise<Event[]> => {
  try {
    const endTimestamp = Timestamp.fromDate(endTime);

    // 查詢可能衝突的事件 (只檢查 timed 事件)
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('timeType', '==', 'timed'),
      where('startTime', '<', endTimestamp),
      orderBy('startTime', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const events = querySnapshot.docs.map(convertDocToEvent);
    
    return events.filter(event => {
      if (excludeEventId && event.id === excludeEventId) {
        return false;
      }
      
      // 檢查實際時間重疊
      const eventEnd = event.endTime || event.startTime;
      return event.startTime < endTime && eventEnd > startTime;
    });
  } catch (error) {
    console.error('Error checking event conflicts:', error);
    throw error;
  }
};

/**
 * 即時監聽指定日期範圍的事件
 * 
 * 注意：由於 Firestore 限制，我們無法直接查詢跨日事件
 * 策略：查詢更大的範圍，然後在客戶端過濾
 */
const subscribeToEventsByDateRange = (
  userId: string,
  startDate: Date,
  endDate: Date,
  callbacks: EventSubscriptionCallbacks
): (() => void) => {
  try {
    // 擴大查詢範圍：向前推30天以捕獲可能的跨日事件
    const extendedStartDate = new Date(startDate);
    extendedStartDate.setDate(extendedStartDate.getDate() - 30);
    
    const startTimestamp = Timestamp.fromDate(extendedStartDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('startTime', '>=', startTimestamp),
      where('startTime', '<=', endTimestamp),
      orderBy('startTime', 'asc')
    );

    return onSnapshot(
      q,
      (querySnapshot) => {
        const allEvents = querySnapshot.docs.map(convertDocToEvent);
        
        // 客戶端過濾：保留與目標日期範圍有重疊的事件
        const filteredEvents = allEvents.filter(event => {
          const eventStart = event.startTime;
          const eventEnd = event.endTime || event.startTime;
          
          // 檢查事件是否與目標日期範圍有重疊
          return eventStart <= endDate && eventEnd >= startDate;
        });
        
        
        callbacks.onSnapshot(filteredEvents);
      },
      (error) => {
        console.error('Error in events subscription:', error);
        callbacks.onError(error);
      }
    );
  } catch (error) {
    console.error('Error setting up events subscription:', error);
    callbacks.onError(error as Error);
    return () => {}; // 返回空的取消函數
  }
};

/**
 * 即時監聽用戶的所有事件
 */
const subscribeToAllEvents = (
  userId: string,
  callbacks: EventSubscriptionCallbacks
): (() => void) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('startTime', 'asc')
    );

    return onSnapshot(
      q,
      (querySnapshot) => {
        const events = querySnapshot.docs.map(convertDocToEvent);
        callbacks.onSnapshot(events);
      },
      (error) => {
        console.error('Error in all events subscription:', error);
        callbacks.onError(error);
      }
    );
  } catch (error) {
    console.error('Error setting up all events subscription:', error);
    callbacks.onError(error as Error);
    return () => {}; // 返回空的取消函數
  }
};

// 導出事件服務
export const eventService = {
  createEvent,
  getEventsByDateRange,
  getEventsByDate,
  getEventById,
  updateEvent,
  deleteEvent,
  createEventsBatch,
  checkEventConflicts,
  subscribeToEventsByDateRange,
  subscribeToAllEvents,
};

// 便利函數：使用當前用戶 ID
export const createEventForCurrentUser = async (eventData: CreateEventRequest): Promise<string> => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return createEvent(userId, eventData);
};

export const getEventsForCurrentUser = async (startDate: Date, endDate: Date): Promise<Event[]> => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return getEventsByDateRange(userId, startDate, endDate);
};